<template>
    <div ref="frame" class="certificate-frame" :style="{ height: frameHeight }">
    <article class="doc-card certificate-sheet" :style="{ transform: 'scale(' + scale + ')' }" aria-label="Certificate of Achievement">
        <svg class="certificate-art" viewBox="0 0 1122 794" aria-hidden="true" focusable="false">
            <path v-for="(path, index) in paths" :key="index" :d="path.d" :fill="palette[path.fill]" :fill-rule="path.rule || 'nonzero'" />
        </svg>
        <div class="certificate-reference">Verification ID: {{ reference }}</div>
        <main class="certificate-content">
            <CredentialBrandLockup class="certificate-brands" :track-logo="trackLogo" :track-name="trackName" :size="53.86" unit="px" />
            <h1>Certificate of Achievement</h1>
            <p class="certificate-caption">Presented to</p>
            <div class="certificate-recipient" :class="{ 'certificate-recipient-long': studentName.length > 38 }">{{ studentName }}</div>
            <p class="certificate-project-label">for the project</p>
            <div class="certificate-project" :class="{ 'certificate-project-long': projectTitle.length > 150 }">{{ projectTitle }}</div>
            <section class="certificate-award">
                <div class="certificate-award-ornament" aria-hidden="true"><span v-for="diamond in award.dots" :key="diamond">◆</span></div>
                <h2>{{ award.name }}</h2>
                <p>{{ award.desc }}</p>
            </section>
        </main>
        <footer class="certificate-verification">
            <div aria-hidden="true"></div>
            <div class="certificate-date"><span>Date of Issue</span><time>{{ issueDate }}</time></div>
            <div class="certificate-stamp"><div class="certificate-stamp-title">NIM Certified</div><DigitalCertificateQr :url="verificationUrl" size="lg" /></div>
        </footer>
    </article>
    </div>
</template>

<script>
import DigitalCertificateQr from './DigitalCertificateQr.vue'
import CredentialBrandLockup from './CredentialBrandLockup.vue'
import { certificateFrame, CERTIFICATE_PALETTE } from '../common/certificateFrame'

const SHEET_WIDTH = 1122
const SHEET_HEIGHT = 794

export default {
    name: 'CertificateDocument',
    components: { DigitalCertificateQr, CredentialBrandLockup },
    props: {
        studentName: { type: String, default: '' }, projectTitle: { type: String, default: '' },
        trackLogo: { type: String, default: '' }, trackName: { type: String, default: '' },
        reference: { type: String, required: true }, verificationUrl: { type: String, required: true },
        issueDate: { type: String, default: '' }, award: { type: Object, required: true },
        waveDepth: { type: Number, default: .13 }, border: { type: Number, default: .035 }
    },
    data: () => ({ palette: CERTIFICATE_PALETTE, scale: 1 }),
    computed: {
        paths() { return certificateFrame({ waveDepth: this.waveDepth, border: this.border }) },
        frameHeight() { return (SHEET_HEIGHT * this.scale).toFixed(2) + 'px' }
    },
    mounted() {
        this.fit()
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.fit())
            this.resizeObserver.observe(this.$refs.frame)
        } else {
            window.addEventListener('resize', this.fit)
        }
    },
    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect()
        window.removeEventListener('resize', this.fit)
    },
    methods: {
        // The sheet is laid out at its full 1122x794 design size and scaled as a whole. Sizing
        // text by container width made it 3-5px on phones, where browsers (notably WeChat)
        // enforce a minimum font size and the layout overflowed.
        fit() {
            const width = this.$refs.frame && this.$refs.frame.clientWidth
            if (width) this.scale = Math.min(1, width / SHEET_WIDTH)
        }
    }
}
</script>

<style scoped>
.certificate-frame { position:relative; flex:none; width:100%; max-width:1122px; overflow:hidden; box-shadow:0 2px 8px rgba(22, 32, 51, 0.10), 0 18px 52px rgba(22, 32, 51, 0.16); }
.certificate-sheet { position:absolute; top:0; left:0; width:1122px; max-width:none; height:794px; transform-origin:0 0; box-shadow:none; background:#fff; color:#17366c; font-family:Outfit,Arial,sans-serif; overflow:hidden; border:0; border-radius:0; padding:0; }
.certificate-art { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; }
.certificate-reference { position:absolute; top:2.5%; left:20%; right:20%; text-align:center; font:400 8.42px/1.3 Arial,sans-serif; color:white; letter-spacing:.025em; }
/* Keep the award as the visual anchor and reserve the lower band for verification. */
.certificate-content { position:absolute; top:16.5%; left:10%; right:10%; text-align:center; }
.certificate-brands { margin-bottom:10.1px; }
.certificate-content h1 { color:#17366c; font:500 21.32px/1.25 Outfit,Arial,sans-serif; letter-spacing:.13em; text-transform:uppercase; margin:0 0 8.98px; }
.certificate-caption { font:400 13.46px/1.35 Outfit,Arial,sans-serif; margin:0; color:#526079; }
.certificate-recipient { font:600 47.12px/1.12 Outfit,Arial,sans-serif; margin:3.93px 0 8.98px; overflow-wrap:anywhere; }
.certificate-recipient-long { font-size:31.42px; }
.certificate-project-label { font-size:12.9px; line-height:1.3; margin:0 0 4.49px; color:#526079; }
.certificate-project { max-width:819.06px; margin:0 auto; font-size:22.44px; font-weight:500; line-height:1.3; text-wrap:balance; overflow-wrap:anywhere; }
.certificate-project-long { font-size:18.51px; }
.certificate-award { position:relative; border:1px solid #e7c66b; border-left:0; border-right:0; background:#fffaf0; margin:19.07px auto 0; padding:10.1px 29.17px 11.22px; }
.certificate-award-ornament { display:flex; justify-content:center; align-items:center; gap:7.29px; color:#bb8618; font-size:8.98px; line-height:1; }
.certificate-award-ornament::before, .certificate-award-ornament::after { content:''; display:block; width:56.1px; height:1px; background:#d7b75d; margin:0 5.61px; }
.certificate-award h2 { color:#17366c; font:600 33.66px/1.2 Outfit,Arial,sans-serif; letter-spacing:.055em; text-transform:uppercase; margin:7.29px 0 9.54px; }
.certificate-award p { font:400 14.03px/1.5 Arial,sans-serif; margin:0 auto; max-width:785.4px; color:#33435a; text-wrap:pretty; }
.certificate-verification { position:absolute; bottom:11.5%; left:11%; right:11%; display:grid; grid-template-columns:1fr 1fr 1fr; align-items:end; gap:22.44px; }
.certificate-stamp { justify-self:end; width:89.76px; background:white; text-align:center; }
.certificate-stamp-title { color:#17366c; font-size:10.1px; line-height:1.4; letter-spacing:.1em; text-transform:uppercase; font-weight:600; }
.certificate-date { text-align:center; padding-bottom:2.24px; }
.certificate-date span { display:block; text-transform:uppercase; font-size:11.22px; letter-spacing:.08em; margin-bottom:4.49px; }
.certificate-date time { display:block; font-size:15.71px; }
.certificate-verification >>> .digital-certificate-qr { justify-self:end; width:89.76px; }
.certificate-verification >>> .digital-certificate-qr__label { font-size:7.85px; line-height:1.3; margin-top:3.37px; }
@media print { .certificate-frame { width:auto !important; max-width:none !important; height:auto !important; overflow:visible !important; box-shadow:none !important; } .certificate-sheet { position:relative !important; transform:none !important; width:297mm !important; max-width:297mm !important; height:210mm !important; margin:0 !important; break-inside:avoid; box-shadow:none; print-color-adjust:exact; -webkit-print-color-adjust:exact; } }
</style>
